import { AttributeType } from "@evergis/api";
import { IconButton } from "@evergis/ui";
import React, { FC, useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { usePostgresLayer } from "../../../../../hooks/layer/usePostgresLayer";
import { LayersSelector } from "../../../../LayersSelector/LayersSelector";
import { Container, Flex } from "../../../EditResource/components/Layer/styled";
import { ResultIcon } from "../../../TableReferences/styled";
import { LayerAttributeSelector } from "../TableReferences/styled";
import { useLayerReferencesContext } from "./LayerReferencesProvider";
import { LayerReferenceItem } from "./types";

const GEOMETRY_TYPES = [
  AttributeType.Point,
  AttributeType.MultiPolygon,
  AttributeType.Multipoint,
  AttributeType.Polyline,
];

export const LayerReference: FC<LayerReferenceItem> = ({ id, referenceAttribute, targetAttribute, layerName }) => {
  const { t } = useTranslation("dialogs");
  const { layerReferences, deleteLayerReference, updateLayerReference, layer } = useLayerReferencesContext();
  const { postgresLayer: referenceLayer, loading: layerInfoLoading } = usePostgresLayer(layerName);
  const referenceAttributeType = useMemo(
    () => (referenceAttribute ? referenceLayer?.layerDefinition?.attributes[referenceAttribute]?.type : null),
    [referenceLayer?.layerDefinition?.attributes, referenceAttribute],
  );
  const isGeometryAttribute = useMemo(
    () => (targetAttribute ? layer.layerDefinition.geometryAttribute === targetAttribute : false),
    [layer, targetAttribute],
  );
  const onLayerNameChange = useCallback(
    (name: string) => {
      updateLayerReference(id, { layerName: name });
    },
    [id, updateLayerReference],
  );
  const onReferenceAttributeChange = useCallback(
    (referenceAttributeName: string) => {
      const attributeType = referenceLayer?.layerDefinition?.attributes[referenceAttributeName]?.type;

      if (GEOMETRY_TYPES.includes(attributeType)) {
        const { geometryAttribute } = layer.layerDefinition;

        updateLayerReference(id, { targetAttribute: geometryAttribute });
      }

      updateLayerReference(id, { referenceAttribute: referenceAttributeName });
    },
    [updateLayerReference, id, referenceLayer, layer],
  );
  const onTargetAttributeChange = useCallback(
    (targetAttributeName: string) => {
      updateLayerReference(id, { targetAttribute: targetAttributeName });
    },
    [updateLayerReference, id],
  );

  useEffect(() => {
    updateLayerReference(id, { layerName });
  }, [layerName, id]);

  return (
    <Container style={{ alignItems: "flex-start", paddingBottom: "1rem" }}>
      <LayersSelector
        id={"referenceLayer"}
        label={`${t("editResource.layerForReference")}`}
        width={"14.75rem"}
        menuMaxHeight="10rem"
        zIndex={120}
        placeholder={t("aggregation.notChosen", { ns: "dialogs" })}
        layer={layerName}
        hiddenLayers={[layer?.name]}
        onChange={layerOption => onLayerNameChange(layerOption.value)}
        withResourceCatalog
      />
      <LayerAttributeSelector
        label={t("tableReferences.attributeForReference")}
        placeholder={t("tableReferences.selectAttributeForReference")}
        layer={referenceLayer}
        attribute={referenceAttribute}
        selectAttribute={onReferenceAttributeChange}
        disabled={!referenceLayer || layerInfoLoading}
        width={"13.75rem"}
      />
      <Flex>
        <ResultIcon kind="link" />
        <LayerAttributeSelector
          label={t("editResource.attributeToRelate")}
          placeholder={t("tableReferences.selectAttributeForReference")}
          layer={layer}
          selectAttribute={onTargetAttributeChange}
          attribute={targetAttribute}
          width={"13.75rem"}
          disabled={!referenceAttribute}
          types={isGeometryAttribute ? GEOMETRY_TYPES : [referenceAttributeType]}
        />
        <IconButton
          style={{ marginLeft: "1rem", marginTop: "1.25rem" }}
          kind={"delete"}
          disabled={layerReferences?.length === 1}
          onClick={() => deleteLayerReference(id)}
        />
      </Flex>
    </Container>
  );
};
